// Import package components
import { useMemo, useContext, useRef, useState, useEffect } from "react";

// Import base components
import { Button } from "../../../ui";
import { AppContexts } from "../../../providers";

const PropertyTag = ({ property, address }) => {
  return (
    <div
      className="uk-inline"
      title={address || undefined}
      uk-tooltip={address || undefined}
    >
      {property.pms_id}
    </div>
  );
};

const Properties = ({ value, onChange, readOnly = false }) => {
  const rentalContext = useContext(AppContexts.RentalsContext);

  const rootRef = useRef(null);
  const [searchValue, setSearchValue] = useState("");
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState(() =>
    Array.isArray(value) ? value : [],
  );

  const properties = useMemo(() => {
    if (!rentalContext.rentals) {
      return [];
    } else {
      return rentalContext.rentals.sort((a, b) =>
        a.pms_id.localeCompare(b.pms_id),
      );
    }
  }, [rentalContext.rentals]);

  const handleInputChange = (tagId, selected) => {
    if (selected) {
      const nextProperties = [
        ...selectedProperties,
        properties.find(
          (tagValue) => tagValue.rental_id.toString() === tagId.toString(),
        ),
      ];
      setSelectedProperties(nextProperties);
      onChange(nextProperties);
    } else {
      const nextProperties = selectedProperties.filter(
        (tag) => tag.rental_id.toString() !== tagId.toString(),
      );
      setSelectedProperties(nextProperties);
      onChange(nextProperties);
    }
  };

  const openProperties = (e) => {
    e.stopPropagation();
    setIsOpen(true);
  };

  useEffect(() => {
    setSelectedProperties(Array.isArray(value) ? value : []);
  }, [value]);

  useEffect(() => {
    setFilteredProperties(
      properties.filter((property) =>
        property.pms_id.toLowerCase().includes(searchValue),
      ),
    );
  }, [properties, searchValue]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const closeProperties = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
        setSearchValue("");
      }
    };

    window.addEventListener("click", closeProperties, true);
    return () => window.removeEventListener("click", closeProperties, true);
  }, [isOpen]);

  const getPropertyAddress = (property) => {
    if (property.address) {
      return property.address;
    }

    const rental = properties.find(
      (rental) =>
        rental.rental_id?.toString() === property.rental_id?.toString() ||
        rental.pms_id === property.pms_id,
    );

    return rental?.address || "";
  };

  return (
    <div
      className="uk-flex uk-flex-top"
      ref={rootRef}
      onClick={openProperties}
    >
      <div>
        <Button>
          <img src="/images/rental.svg" alt="Select properties" />
        </Button>
        <div
          className={`uk-dropdown guest-tags-dropdown${isOpen ? " uk-open" : ""}`}
        >
          <div className="uk-search uk-search-default uk-margin-small-bottom">
            <span
              className="uk-search-icon-flip uk-icon uk-search-icon"
              uk-search-icon="true"
              onClick={() => setSearchValue("")}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  fill="none"
                  stroke="#000"
                  strokeWidth="1.1"
                  cx="9"
                  cy="9"
                  r="7"
                />
                <path
                  fill="none"
                  stroke="#000"
                  strokeWidth="1.1"
                  d="M14,14 L18,18 L14,14 Z"
                />
              </svg>
            </span>
            <input
              className="uk-input"
              type="input"
              placeholder="Search"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
            />
          </div>
          <ul className="uk-nav uk-dropdown-nav tags-selector">
            {filteredProperties.map(
              (property, index) =>
                property.pms_id !== "" && (
                  <li key={index}>
                    <label>
                      <input
                        className="uk-checkbox"
                        type="checkbox"
                        checked={
                          selectedProperties.findIndex(
                            (tag) =>
                              tag.rental_id.toString() ===
                              property.rental_id.toString(),
                          ) >= 0
                        }
                        onChange={
                          readOnly
                            ? () => {}
                            : (event) =>
                                handleInputChange(
                                  property.rental_id,
                                  event.target.checked,
                                )
                        }
                      />
                      {property.pms_id}
                    </label>
                  </li>
                ),
            )}
          </ul>
        </div>
      </div>
      <div className="hms-guest-tags">
        <div className="guest-tags">
          {selectedProperties.map((property, index) => {
            const address = getPropertyAddress(property);

            return (
              <PropertyTag
                key={property.rental_id || property.pms_id || index}
                property={property}
                address={address}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Properties;
